Returns organization users that may be referenced by public API transfer targets.

Inactive users are included so external systems can reconcile ids and operational availability without losing historical references.

<ApiCodeExamples exampleId="usersList" />

<ApiResponseExamples exampleId="usersList" />

## Required scope

- `users.read`

## Endpoints

- `GET /public/v1/users/`: paginated list.
- `GET /public/v1/users/{id}/`: detail for one user.

The detail response uses the same field shape as the list item.

## Query parameters

- `page` (`integer`, optional): page number.
- `page_size` (`integer`, optional): items per page. Default `25`, maximum `100`.
- `search` (`string`, optional): searches by first name, last name, and email.
- `ordering` (`string`, optional): `email`, `first_name`, `last_name`, `last_login`, `telephony_availability_updated_at`, or the same fields prefixed with `-`.
- `transfer_eligible` (`boolean`, optional): `true` for active users with an eligible endpoint, `false` for users that cannot receive transfer right now.

## Response fields

- `id` (`uuid`): user public id for `transfer_target.id`.
- `name` (`string`): display name.
- `email` (`string`): user email.
- `is_active` (`boolean`): whether the user account is active.
- `transfer_eligible` (`boolean`): whether the user can currently be used as transfer target.
- `telephony_endpoint` (`object|null`): active eligible endpoint selected for transfer display.
- `telephony_endpoint.id` (`uuid`): endpoint public id.
- `telephony_endpoint.endpoint_type` (`string`): endpoint type.
- `telephony_endpoint.extension` (`string|null`): internal extension when present.
- `telephony_endpoint.external_phone_e164` (`string|null`): external phone when present.
- `last_login_at` (`datetime|null`): last user login.
- `updated_at` (`datetime|null`): last telephony-availability update used by this catalog.

## Option reference

- `telephony_endpoint.endpoint_type`: `internal_extension` or `external_extension`.
- `transfer_eligible=true` requires an active user and an active eligible transfer endpoint.

## Expected errors

- `401 Unauthorized`: invalid, expired, revoked, missing, or IP-blocked token.
- `403 Forbidden`: the credential does not have `users.read`.
- `404 Not Found`: detail id is not available to the organization.
- `429 Too Many Requests`: credential rate limit exceeded.
